import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { RootState } from '../../app/store'
import { CheckinEntry } from '../../app/types'

export enum PanelState {
  // eslint-disable-next-line
  HIDDEN = 'hidden',
  // eslint-disable-next-line
  EDIT = 'edit',
  // eslint-disable-next-line
  PROCESSING = 'processing',
  // eslint-disable-next-line
  ERROR = 'error',
}

export enum BibFieldError {
  // eslint-disable-next-line
  NONE = 'none',
  // eslint-disable-next-line
  ZERO = 'bib-zero-error',
  // eslint-disable-next-line
  DUPLICATE = 'bib-error',
}

export enum TagFieldError {
  // eslint-disable-next-line
  NONE = 'none',
  // eslint-disable-next-line
  DUPLICATE = 'tag-error',
}

type CheckinState = {
  dirtyBib?: string
  dirtyTag?: string
  dirtyInput: boolean
  panelState: PanelState
  bibFieldError: BibFieldError
  tagFieldError: TagFieldError
  selectedEntry?: CheckinEntry
  selectedEntryTeam?: CheckinEntry
  qrPaused: boolean
  enterKeyPress: boolean
  cameraFacingMode: string
  isAlertUnsavedChangesOpen: boolean
  isAlertNoBibNumberOpen: boolean
  isAlertNoTagNumberOpen: boolean
  isAlertMakeBibTagAvailableOpen: boolean
}

const initialState: CheckinState = {
  dirtyBib: undefined,
  dirtyTag: undefined,
  dirtyInput: false,
  panelState: PanelState.HIDDEN,
  bibFieldError: BibFieldError.NONE,
  tagFieldError: TagFieldError.NONE,
  selectedEntry: undefined,
  selectedEntryTeam: undefined,
  qrPaused: true,
  enterKeyPress: false,
  cameraFacingMode: 'environment',
  isAlertUnsavedChangesOpen: false,
  isAlertNoBibNumberOpen: false,
  isAlertNoTagNumberOpen: false,
  isAlertMakeBibTagAvailableOpen: false,
}

export const checkinSlice = createSlice({
  name: 'checkin',
  initialState,
  reducers: {
    setDirtyBib: (state, action: PayloadAction<string | undefined>) => {
      state.dirtyBib = action.payload
    },
    setDirtyTag: (state, action: PayloadAction<string | undefined>) => {
      state.dirtyTag = action.payload
    },
    setDirtyInput: (state, action: PayloadAction<boolean>) => {
      state.dirtyInput = action.payload
    },
    setEnterKeyPress: (state, action: PayloadAction<boolean>) => {
      state.enterKeyPress = action.payload
    },
    setPanelState: (state, action: PayloadAction<PanelState>) => {
      state.panelState = action.payload
    },
    setBibFieldError: (state, action: PayloadAction<BibFieldError>) => {
      state.bibFieldError = action.payload
    },
    setTagFieldError: (state, action: PayloadAction<TagFieldError>) => {
      state.tagFieldError = action.payload
    },
    setSelectedEntry: (
      state,
      action: PayloadAction<CheckinEntry | undefined>
    ) => {
      state.selectedEntry = action.payload
      state.dirtyBib = action.payload?.bib
      state.dirtyTag = action.payload?.tag
      state.bibFieldError = BibFieldError.NONE
      state.tagFieldError = TagFieldError.NONE
    },
    setSelectedEntryTeam: (
      state,
      action: PayloadAction<CheckinEntry | undefined>
    ) => {
      state.selectedEntryTeam = action.payload
      if (action.payload?.teamType === 'RELAY' && action.payload?.teamShareBib) {
        state.dirtyBib = action.payload?.bib
      }
    },
    setQRPaused: (state, action: PayloadAction<boolean>) => {
      state.qrPaused = action.payload
    },
    setCameraFacingMode: (state, action: PayloadAction<string>) => {
      state.cameraFacingMode = action.payload
    },
    setIsAlertUnsavedChangesOpen: (state, action: PayloadAction<boolean>) => {
      state.isAlertUnsavedChangesOpen = action.payload
    },
    setIsAlertNoBibNumberOpen: (state, action: PayloadAction<boolean>) => {
      state.isAlertNoBibNumberOpen = action.payload
    },
    setIsAlertNoTagNumberOpen: (state, action: PayloadAction<boolean>) => {
      state.isAlertNoTagNumberOpen = action.payload
    },
    setIsAlertMakeBibTagAvailableOpen: (state, action: PayloadAction<boolean>) => {
      state.isAlertMakeBibTagAvailableOpen = action.payload
    },
  },
})

export const {
  setDirtyBib,
  setDirtyTag,
  setDirtyInput,
  setPanelState,
  setBibFieldError,
  setTagFieldError,
  setSelectedEntry,
  setSelectedEntryTeam,
  setQRPaused,
  setEnterKeyPress,
  setCameraFacingMode,
  setIsAlertUnsavedChangesOpen,
  setIsAlertNoBibNumberOpen,
  setIsAlertNoTagNumberOpen,
  setIsAlertMakeBibTagAvailableOpen,
} = checkinSlice.actions

export const getCheckedInSelector = (state: RootState) =>
  state.checkin.selectedEntry?.checkedIn ?? false
export const getDirtyBibSelector = (state: RootState) => state.checkin.dirtyBib
export const getDirtyTagSelector = (state: RootState) => state.checkin.dirtyTag
export const getDirtyInputSelector = (state: RootState) =>
  state.checkin.dirtyInput
export const getPanelStateSelector = (state: RootState): PanelState =>
  state.checkin.panelState
export const getBibFieldErrorSelector = (state: RootState): BibFieldError =>
  state.checkin.bibFieldError
export const getTagFieldErrorSelector = (state: RootState): TagFieldError =>
  state.checkin.tagFieldError
export const getSelectedEntrySelector = (state: RootState) =>
  state.checkin.selectedEntry
export const getSelectedEntryTeamSelector = (state: RootState) =>
  state.checkin.selectedEntryTeam
export const getSelectedEntryIdSelector = (state: RootState) =>
  state.checkin.selectedEntry?.entryId
export const getQRPausedSelector = (state: RootState) => state.checkin.qrPaused
export const getEnterKeyPressSelector = (state: RootState) =>
  state.checkin.enterKeyPress
export const getCameraFacingMode = (state: RootState) =>
  state.checkin.cameraFacingMode
export const getIsAlertUnsavedChangesOpenSelector = (state: RootState) =>
  state.checkin.isAlertUnsavedChangesOpen
export const getIsAlertNoBibNumberOpenSelector = (state: RootState) =>
  state.checkin.isAlertNoBibNumberOpen
export const getIsAlertNoTagNumberOpenSelector = (state: RootState) =>
  state.checkin.isAlertNoTagNumberOpen
export const getIsAlertMakeBibTagAvailableOpenSelector = (state: RootState) =>
  state.checkin.isAlertMakeBibTagAvailableOpen
export default checkinSlice.reducer

export const isBibZeroError = (
  bibFieldError: BibFieldError
): bibFieldError is BibFieldError.ZERO =>
  bibFieldError === BibFieldError.ZERO
export const isBibError = (
  bibFieldError: BibFieldError
): bibFieldError is BibFieldError.DUPLICATE =>
  bibFieldError === BibFieldError.DUPLICATE

export const isTagError = (
  tagFieldError: TagFieldError
): tagFieldError is TagFieldError.DUPLICATE =>
  tagFieldError === TagFieldError.DUPLICATE
